import _ from "lodash";
import React, { useMemo } from "react";
import classes from "./ToddleAcademyThirdPane.scss";
import I18nHOC from "UIComponents/I18nHOC";
import { openLinkInNewTab, goToRelativeRoute } from "modules/Services";
import { goToPlatformHomeChildRoute } from "modules/NavigationModule";
import { getUserInfo } from "Login/modules/LoginModule/LoginUtils";
import ACLStore from "lib/aclStore";
import { compose } from "lib/apolloClient";
import { connect } from "react-redux";
import {
  ArrowOutOutlined,
  ToddleAcademyDuotone,
  HelpCenterDuotone,
  WhatsNewDuotone,
  CloseOutlined,
} from "@toddle-edu/ds-icons";
import { IconButton } from "@toddle-edu/ds-web";
import { getWorkrampLoginUrl } from "Courses/modules/CoursesModule";
import { CURRICULUM_TYPE_UBD } from "Constants/stringConstants";
import { getIsAccountConfigurationEnabled } from "Utils/ACLStoreUtils";
import { getIsChina } from "config";

export const getHelCenterUrl = isUBD => {
  const isChina = getIsChina();

  if (getIsAccountConfigurationEnabled()) {
    return isChina
      ? "https://helpcenter.toddleapp.cn"
      : "https://helpcenter.toddleapp.com";
  }

  if (isChina) {
    return isUBD ? "https://help.toddleapp.cn" : "https://support.toddleapp.cn";
  }

  return isUBD
    ? "https://help.toddleapp.com/"
    : "https://support.toddleapp.com/";
};

const getOptions = (getToddleAcademyRoute, isUBD) => [
  {
    label: "common:toddle_academy",
    desc: "common:toddle_academy_desc",
    icon: <ToddleAcademyDuotone color="teal" size={"large"} />,
    hoverIcon: <ArrowOutOutlined />,
    uid: "TODDLE_ACADEMY",
    openLinkInNewTab: true,
    perm: "FeatureFlag:WorkrampAcademy",
    userTypes: ["staff"],
    route: () => getToddleAcademyRoute(),
  },
  {
    label: "teacherHomePage:helpCenter",
    desc: "common:help_center_desc",
    hoverIcon: <ArrowOutOutlined />,
    icon: <HelpCenterDuotone color="violet" size={"large"} />,
    uid: "HELP_CENTRE",
    perm: "Common:EnableHelpCenter",
    openLinkInNewTab: true,
    userTypes: ["staff"],
    route: getHelCenterUrl(isUBD),
  },
  {
    label: "common:product_updates",
    desc: "common:whats_new_description",
    icon: <WhatsNewDuotone color="yellow" size={"large"} />,
    uid: "WHATS_NEW",
    perm: "Common:ShowProductUpdates",
    route: "product-updates",
    userTypes: ["staff", "parent", "student"],
  },
];

const Item = React.memo(
  ({
    item,
    openLinkInNewTab,
    goToRelativeRoute,
    isAdminPage,
    t,
    goToPlatformHomeChildRoute,
  }) => {
    const onClick = async () => {
      if (item.openLinkInNewTab) {
        openLinkInNewTab({
          url:
            typeof item.route === "function" ? await item.route() : item.route,
        });
      } else {
        if (isAdminPage) {
          goToRelativeRoute({ route: item.route });
        } else {
          goToPlatformHomeChildRoute({ route: item.route });
        }
      }
    };
    const hasHoverIcon = item.hoverIcon;
    return (
      <div className={classes.item} onClick={onClick}>
        <div className={classes.iconContainer}>{item.icon}</div>
        <div className={classes.detailsContainer}>
          <div className={classes.label}>{t(item.label)}</div>
          <div className={classes.description}>{t(item.desc)}</div>
        </div>
        {hasHoverIcon ? (
          <div className={classes.hoverIconContainer}>{item.hoverIcon}</div>
        ) : null}
      </div>
    );
  }
);
Item.displayName = "Item";

const ToddleAcademyThirdPane = ({
  onClose,
  openLinkInNewTab,
  goToRelativeRoute,
  isAdminPage,
  t,
  userType,
  getWorkrampLoginUrl,
  userId,
  userEntityType,
  goToPlatformHomeChildRoute,
  isUBD,
}) => {
  const getToddleAcademyRoute = async () =>
    await getWorkrampLoginUrl({ id: userId, type: userEntityType });

  const options = useMemo(() => getOptions(getToddleAcademyRoute, isUBD), [
    isUBD,
  ]);
  const fileredOptions = _.filter(options, item => {
    if (_.includes(item.userTypes, userType)) {
      return item.perm ? ACLStore.can(item.perm) : true;
    } else {
      return false;
    }
  });
  return (
    <div className={classes.container}>
      <div className={classes.header}>
        <div className={classes.headerText}>
          {t("common:learn_and_inspire")}
        </div>
        <IconButton
          icon={<CloseOutlined />}
          onClick={onClose}
          variant="plain"
        />
      </div>
      <div className={classes.body}>
        {_.map(fileredOptions, item => (
          <Item
            key={_.get(item, "uid")}
            item={item}
            openLinkInNewTab={openLinkInNewTab}
            t={t}
            goToPlatformHomeChildRoute={goToPlatformHomeChildRoute}
            goToRelativeRoute={goToRelativeRoute}
            isAdminPage={isAdminPage}
          />
        ))}
      </div>
    </div>
  );
};

const mapActionCreators = {
  openLinkInNewTab,
  getWorkrampLoginUrl,
  goToPlatformHomeChildRoute,
  goToRelativeRoute,
};

const mapStateToProps = state => {
  const userId = _.get(getUserInfo({ portalType: "PLANNER" }), "id", "");
  const userEntityType = _.get(
    getUserInfo({ portalType: "PLANNER" }),
    "userEntityType",
    ""
  );

  const activeTab = _.get(state, "login.activeTab", "");
  const isAdminPage = activeTab === "admin";
  const curriculumType = _.get(state, "platform.currentCurriculumProgram.type");
  const isUBD = _.isEqual(curriculumType, CURRICULUM_TYPE_UBD);
  return {
    userType: state.login.userInfo.user_type,
    userId,
    userEntityType,
    isAdminPage,
    isUBD,
  };
};

export default compose(
  connect(mapStateToProps, mapActionCreators),
  I18nHOC({ resource: ["common", "teacherHomePage"] })
)(ToddleAcademyThirdPane);
